import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import ReactQuill from 'react-quill';
import 'quill/dist/quill.snow.css';
import api from '../services/api';
import { useSnackbar } from '../context/SnackbarContext';
import './AdminProducts.css';

const BULK_UPLOAD_RESULT_KEY = 'adminBulkUploadResult';
const BULK_UPLOAD_RESULT_TTL_MS = 5 * 60 * 1000; // 5 minutes

interface Product {
  id: string;
  name: string;
  description: string;
  price: number;
  category: string;
  categoryId?: string;
  stock: number;
  imageUrl?: string;
  isVisible?: boolean;
}

const AdminProducts: React.FC = () => {
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingProduct, setEditingProduct] = useState<Product | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [visibilityFilter, setVisibilityFilter] = useState<'all' | 'visible' | 'hidden'>('all');
  const [deleteTarget, setDeleteTarget] = useState<Product | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [categoryOptions, setCategoryOptions] = useState<string[]>([]);
  const { showSuccess, showError } = useSnackbar();
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    price: '',
    category: '',
    stock: '',
    imageUrl: '',
  });
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string>('');
  const [imageUploading, setImageUploading] = useState(false);
  const [cloudinaryImages, setCloudinaryImages] = useState<string[]>([]);
  const [cloudinaryLoading, setCloudinaryLoading] = useState(false);
  const [cloudinaryError, setCloudinaryError] = useState('');
  const [showCloudinaryPicker, setShowCloudinaryPicker] = useState(false);
  const [bulkFile, setBulkFile] = useState<File | null>(null);
  const [bulkUploading, setBulkUploading] = useState(false);
  const [bulkResult, setBulkResult] = useState<string>('');
  const [descriptionGenerating, setDescriptionGenerating] = useState(false);
  const formRef = useRef<HTMLDivElement | null>(null);
  const isFormDirty = useMemo(() => {
    if (!showForm) return false;
    if (editingProduct) {
      return (
        formData.name !== editingProduct.name ||
        formData.description !== editingProduct.description ||
        formData.price !== editingProduct.price.toString() ||
        formData.category !== editingProduct.category ||
        formData.stock !== editingProduct.stock.toString() ||
        formData.imageUrl !== (editingProduct.imageUrl || '') ||
        Boolean(imageFile)
      );
    }
    return (
      formData.name !== '' ||
      formData.description !== '' ||
      formData.price !== '' ||
      formData.category !== '' ||
      formData.stock !== '' ||
      formData.imageUrl !== '' ||
      Boolean(imageFile)
    );
  }, [editingProduct, formData, imageFile, showForm]);

  const fetchProducts = useCallback(async () => {
    try {
      const response = await api.get('/admin/products', {
        params: {
          page: currentPage,
          limit: itemsPerPage,
          search: searchQuery || undefined,
          category: selectedCategory !== 'all' ? selectedCategory : undefined,
          visibility: visibilityFilter !== 'all' ? visibilityFilter : undefined,
        },
      });
      setProducts(response.data.products || []);
      setTotalCount(Number(response.data.total || 0));
      setCategoryOptions(response.data.categories || []);
    } catch (error) {
      console.error('Error fetching products:', error);
    } finally {
      setLoading(false);
    }
  }, [currentPage, itemsPerPage, searchQuery, selectedCategory, visibilityFilter]);

  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, selectedCategory, visibilityFilter]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const plainDescription = formData.description
      .replace(/<[^>]*>/g, ' ')
      .replace(/&nbsp;/g, ' ')
      .trim();
    if (!formData.name || !plainDescription || !formData.price || !formData.category || !formData.stock) {
      showError('Please complete all required fields');
      return;
    }
    try {
      if (editingProduct) {
        await api.put(`/admin/products/${editingProduct.id}`, formData);
      } else {
        await api.post('/admin/products', formData);
      }
      setShowForm(false);
      setEditingProduct(null);
      resetForm();
      fetchProducts();
      showSuccess(editingProduct ? 'Product updated successfully' : 'Product created successfully');
    } catch (error: any) {
      showError(error.response?.data?.error || 'Failed to save product');
    }
  };

  const openForm = (nextProduct?: Product) => {
    if (isFormDirty) {
      const isSameProduct = Boolean(nextProduct && editingProduct?.id === nextProduct.id);
      if (!isSameProduct) {
        const proceed = window.confirm('You have unsaved changes. Discard them and continue?');
        if (!proceed) return;
      }
    }
    if (nextProduct) {
      setEditingProduct(nextProduct);
      setFormData({
        name: nextProduct.name,
        description: nextProduct.description,
        price: nextProduct.price.toString(),
        category: nextProduct.category,
        stock: nextProduct.stock.toString(),
        imageUrl: nextProduct.imageUrl || '',
      });
      setImagePreview(nextProduct.imageUrl || '');
    } else {
      setEditingProduct(null);
      resetForm();
      setImagePreview('');
    }
    setShowForm(true);
    window.setTimeout(() => {
      formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 0);
  };

  const handleEdit = (product: Product) => {
    openForm(product);
  };

  const handleGenerateDescription = async () => {
    const name = formData.name.trim();
    if (!name) {
      showError('Enter a product name first, then generate description.');
      return;
    }
    setDescriptionGenerating(true);
    try {
      const { data } = await api.post<{ description: string }>('/admin/products/generate-description', {
        name,
        category: formData.category.trim() || undefined,
      });
      if (data.description) {
        setFormData((prev) => ({ ...prev, description: data.description }));
        showSuccess('Description generated. You can edit it before saving.');
      } else {
        showError('No description returned. Try again or write it manually.');
      }
    } catch (err: any) {
      const msg = err.response?.data?.error || 'Failed to generate description. Try again or write it manually.';
      showError(msg);
    } finally {
      setDescriptionGenerating(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await api.delete(`/admin/products/${deleteTarget.id}`);
      fetchProducts();
      showSuccess('Product deleted successfully');
      setDeleteTarget(null);
    } catch (error: any) {
      showError(error.response?.data?.error || 'Failed to delete product');
    } finally {
      setDeleting(false);
    }
  };

  const handleToggleVisibility = async (product: Product) => {
    const nextVisible = !product.isVisible;
    setProducts(prev =>
      prev.map(item =>
        item.id === product.id ? { ...item, isVisible: nextVisible } : item
      )
    );
    try {
      await api.put(`/admin/products/${product.id}`, { isVisible: nextVisible });
      showSuccess(`Product ${nextVisible ? 'visible' : 'hidden'} successfully`);
    } catch (error: any) {
      setProducts(prev =>
        prev.map(item =>
          item.id === product.id ? { ...item, isVisible: product.isVisible } : item
        )
      );
      showError(error.response?.data?.error || 'Failed to update visibility');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      price: '',
      category: '',
      stock: '',
      imageUrl: '',
    });
    setImageFile(null);
    setImagePreview('');
    setImageUploading(false);
  };

  const handleImageSelect = (file: File | null) => {
    setImageFile(file);
    if (file) {
      setImagePreview(URL.createObjectURL(file));
    } else {
      setImagePreview('');
    }
  };

  const uploadImage = async () => {
    if (!imageFile) {
      showError('Select an image to upload');
      return;
    }

    setImageUploading(true);
    try {
      const signatureResponse = await api.post('/admin/images/signature');
      const { cloudName, apiKey, timestamp, signature, folder } = signatureResponse.data;

      const formDataUpload = new FormData();
      formDataUpload.append('file', imageFile);
      formDataUpload.append('api_key', apiKey);
      formDataUpload.append('timestamp', String(timestamp));
      formDataUpload.append('signature', signature);
      if (folder) {
        formDataUpload.append('folder', folder);
      }

      const uploadResponse = await fetch(
        `https://api.cloudinary.com/v1_1/${cloudName}/image/upload`,
        {
          method: 'POST',
          body: formDataUpload,
        }
      );

      if (!uploadResponse.ok) {
        throw new Error('Upload failed');
      }

      const data = await uploadResponse.json();
      setFormData((prev) => ({ ...prev, imageUrl: data.secure_url }));
      setImagePreview(data.secure_url);
      setImageFile(null);
      showSuccess('Image uploaded');
    } catch (error: any) {
      showError(error.message || 'Failed to upload image');
    } finally {
      setImageUploading(false);
    }
  };

  const fetchCloudinaryImages = async () => {
    setCloudinaryLoading(true);
    setCloudinaryError('');
    try {
      const response = await api.get('/admin/images/list');
      const urls = response.data.images?.map((img: any) => img.secureUrl).filter(Boolean) || [];
      setCloudinaryImages(urls);
    } catch (error: any) {
      setCloudinaryError(error.response?.data?.error || 'Failed to load Cloudinary images');
    } finally {
      setCloudinaryLoading(false);
    }
  };

  useEffect(() => {
    try {
      const raw = sessionStorage.getItem(BULK_UPLOAD_RESULT_KEY);
      if (!raw) return;
      const data = JSON.parse(raw) as { createdCount?: number; errorCount?: number; error?: string; at: number };
      if (Date.now() - data.at > BULK_UPLOAD_RESULT_TTL_MS) {
        sessionStorage.removeItem(BULK_UPLOAD_RESULT_KEY);
        return;
      }
      sessionStorage.removeItem(BULK_UPLOAD_RESULT_KEY);
      if (data.error) {
        showError(data.error);
      } else if (data.createdCount !== undefined) {
        showSuccess(
          `Bulk upload completed: ${data.createdCount} products created${data.errorCount ? `, ${data.errorCount} errors` : ''}.`
        );
      }
    } catch {
      sessionStorage.removeItem(BULK_UPLOAD_RESULT_KEY);
    }
  }, [showSuccess, showError]);

  const uploadBulkProducts = () => {
    if (!bulkFile) {
      showError('Select a CSV file to upload');
      return;
    }
    setBulkUploading(true);
    setBulkResult('');
    const fileToUpload = bulkFile;
    api
      .post('/admin/products/bulk-upload', (() => {
        const formDataUpload = new FormData();
        formDataUpload.append('file', fileToUpload);
        return formDataUpload;
      })(), {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      .then((response) => {
        const { createdCount, errors } = response.data;
        const errorCount = errors?.length || 0;
        setBulkResult(`Created ${createdCount} products. ${errorCount} errors.`);
        if (errors?.length) {
          console.error('Bulk upload errors:', errors);
        }
        showSuccess(
          `Bulk upload completed: ${createdCount} products created${errorCount ? `, ${errorCount} errors` : ''}.`
        );
        try {
          sessionStorage.setItem(
            BULK_UPLOAD_RESULT_KEY,
            JSON.stringify({ createdCount, errorCount, at: Date.now() })
          );
        } catch {}
        fetchProducts();
        setBulkFile(null);
      })
      .catch((error: any) => {
        const message = error.response?.data?.error || 'Bulk upload failed';
        showError(message);
        try {
          sessionStorage.setItem(
            BULK_UPLOAD_RESULT_KEY,
            JSON.stringify({ error: message, at: Date.now() })
          );
        } catch {}
      })
      .finally(() => {
        setBulkUploading(false);
      });
  };

  // Pagination calculations
  const categories = ['all', ...categoryOptions];
  const totalPages = Math.max(1, Math.ceil(totalCount / itemsPerPage));
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + products.length;
  const currentProducts = products;
  const hasFilters =
    Boolean(searchQuery) || selectedCategory !== 'all' || visibilityFilter !== 'all';

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleItemsPerPageChange = (items: number) => {
    setItemsPerPage(items);
    setCurrentPage(1); // Reset to first page when changing items per page
  };

  // Reset to page 1 when products change (e.g., after delete)
  useEffect(() => {
    if (currentPage > totalPages && totalPages > 0) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  if (loading) {
    return (
      <div className="container">
        <div className="product-skeleton-grid">
          {[0, 1, 2].map((item) => (
            <div key={item} className="product-skeleton-card">
              <div className="product-skeleton-line title" />
              <div className="product-skeleton-line meta" />
              <div className="product-skeleton-line meta short" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="container">
      {bulkUploading && (
        <div className="bulk-upload-banner" role="status" aria-live="polite">
          <span className="bulk-upload-banner-spinner" aria-hidden="true" />
          <span>
            Bulk upload in progress… You can continue using the page. We&apos;ll notify you when it&apos;s done.
          </span>
        </div>
      )}
      <div className="admin-products-header">
        <h1>Manage Products</h1>
        <button className="btn btn-primary" onClick={() => openForm()}>
          Add New Product
        </button>
      </div>

      <div className="product-filters">
        <div className="filter-search">
          <input
            type="text"
            placeholder="Search products..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
        <div className="filter-select-group">
          <label>Category</label>
          <select value={selectedCategory} onChange={(e) => setSelectedCategory(e.target.value)}>
            {categories.map(category => (
              <option key={category} value={category}>
                {category === 'all' ? 'All Categories' : category}
              </option>
            ))}
          </select>
        </div>
        <div className="filter-select-group">
          <label>Visibility</label>
          <select value={visibilityFilter} onChange={(e) => setVisibilityFilter(e.target.value as 'all' | 'visible' | 'hidden')}>
            <option value="all">All</option>
            <option value="visible">Visible</option>
            <option value="hidden">Hidden</option>
          </select>
        </div>
        <button
          type="button"
          className="btn btn-secondary btn-clear-filters"
          onClick={() => {
            setSearchQuery('');
            setSelectedCategory('all');
            setVisibilityFilter('all');
          }}
        >
          Clear Filters
        </button>
      </div>

      {showForm && (
        <div className="product-form-card" ref={formRef}>
          <h2>{editingProduct ? 'Edit Product' : 'Add New Product'}</h2>
          <form onSubmit={handleSubmit}>
            <div className="form-row">
              <div className="form-group">
                <label>Product Name *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                />
              </div>
              <div className="form-group">
                <label>Category *</label>
                <input
                  type="text"
                  value={formData.category}
                  onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                  required
                />
              </div>
            </div>
            <div className="form-group">
              <div className="description-label-row">
                <label>Description *</label>
                <button
                  type="button"
                  className="btn btn-secondary btn-generate-description"
                  onClick={handleGenerateDescription}
                  disabled={descriptionGenerating || !formData.name.trim()}
                  title="Generate a short product description using AI (requires product name)"
                >
                  {descriptionGenerating ? 'Generating…' : 'Generate with AI'}
                </button>
              </div>
              <div className="product-wysiwyg">
                <ReactQuill
                  theme="snow"
                  value={formData.description}
                  onChange={(value: string) => setFormData({ ...formData, description: value })}
                  placeholder="Write a detailed description or use Generate with AI..."
                />
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>Base Price ($) *</label>
                <input
                  type="number"
                  step="0.01"
                  value={formData.price}
                  onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                  required
                />
              </div>
              <div className="form-group">
                <label>Stock *</label>
                <input
                  type="number"
                  value={formData.stock}
                  onChange={(e) => setFormData({ ...formData, stock: e.target.value })}
                  required
                  min="0"
                />
              </div>
            </div>
            <div className="form-group">
              <label>Product Image</label>
              {imagePreview && (
                <div className="image-preview">
                  <img src={imagePreview} alt="Preview" />
                </div>
              )}
              <input
                type="file"
                accept="image/*"
                className="image-file-input"
                onChange={(e) => handleImageSelect(e.target.files?.[0] || null)}
              />
              <div className="image-actions">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={uploadImage}
                  disabled={imageUploading || !imageFile}
                >
                  {imageUploading ? 'Uploading...' : 'Upload to Cloudinary'}
                </button>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => {
                    setShowCloudinaryPicker((prev) => !prev);
                    if (!cloudinaryImages.length) {
                      fetchCloudinaryImages();
                    }
                  }}
                >
                  {showCloudinaryPicker ? 'Hide Cloudinary Library' : 'Browse Cloudinary'}
                </button>
                {formData.imageUrl && (
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => {
                      setFormData((prev) => ({ ...prev, imageUrl: '' }));
                      setImagePreview('');
                      setImageFile(null);
                    }}
                  >
                    Remove Image
                  </button>
                )}
              </div>
              {showCloudinaryPicker && (
                <div className="cloudinary-panel">
                  {cloudinaryLoading && <div className="cloudinary-loading">Loading images...</div>}
                  {cloudinaryError && <div className="error">{cloudinaryError}</div>}
                  {!cloudinaryLoading && cloudinaryImages.length === 0 && !cloudinaryError && (
                    <div className="cloudinary-empty">No images found in Cloudinary.</div>
                  )}
                  <div className="cloudinary-grid">
                    {cloudinaryImages.map((url) => (
                      <button
                        key={url}
                        type="button"
                        className="cloudinary-tile"
                        onClick={() => {
                          setFormData((prev) => ({ ...prev, imageUrl: url }));
                          setImagePreview(url);
                          setShowCloudinaryPicker(false);
                        }}
                      >
                        <img src={url} alt="Cloudinary" />
                      </button>
                    ))}
                  </div>
                </div>
              )}
              <div className="form-group image-url-field">
                <label>Image URL (optional)</label>
                <input
                  type="url"
                  value={formData.imageUrl}
                  onChange={(e) => {
                    setFormData({ ...formData, imageUrl: e.target.value });
                    setImagePreview(e.target.value);
                  }}
                  placeholder="https://example.com/image.jpg"
                />
              </div>
            </div>
            <div className="form-actions">
              <button type="submit" className="btn btn-primary">
                {editingProduct ? 'Update Product' : 'Create Product'}
              </button>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => { setShowForm(false); setEditingProduct(null); resetForm(); }}
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="products-table-container">
        <div className="table-header-controls">
          <div className="pagination-info">
            {totalCount === 0
              ? hasFilters
                ? 'No products match these filters'
                : 'No products yet'
              : `Showing ${startIndex + 1}-${Math.min(endIndex, totalCount)} of ${totalCount} products`}
          </div>
          <div className="items-per-page">
            <label>Items per page:</label>
            <select
              value={itemsPerPage}
              onChange={(e) => handleItemsPerPageChange(Number(e.target.value))}
              className="items-per-page-select"
            >
              <option value={5}>5</option>
              <option value={10}>10</option>
              <option value={20}>20</option>
              <option value={50}>50</option>
            </select>
          </div>
        </div>

        <table className="products-table">
          <thead>
            <tr>
              <th>Image</th>
              <th>Name</th>
              <th>Category</th>
              <th>Base Price</th>
              <th>Stock</th>
              <th>Visibility</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {currentProducts.length === 0 ? (
              <tr>
                <td colSpan={7} className="no-products">
                  <div className="no-products-icon">🧺</div>
                  <h3>{hasFilters ? 'No products match these filters' : 'No products yet'}</h3>
                  <p>
                    {hasFilters
                      ? 'Try adjusting the filters to find products.'
                      : 'Add your first product to get started.'}
                  </p>
                  <div className="no-products-actions">
                    {!hasFilters ? (
                      <button type="button" className="btn btn-primary" onClick={() => openForm()}>
                        Add Product
                      </button>
                    ) : (
                      <button
                        type="button"
                        className="btn btn-secondary"
                        onClick={() => {
                          setSearchQuery('');
                          setSelectedCategory('all');
                          setVisibilityFilter('all');
                        }}
                      >
                        Clear filters
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ) : (
              currentProducts.map((product) => (
                <tr key={product.id}>
                  <td>
                    {product.imageUrl ? (
                      <img src={product.imageUrl} alt={product.name} className="product-thumb" />
                    ) : (
                      <div className="product-thumb-placeholder">No Image</div>
                    )}
                  </td>
                  <td>{product.name}</td>
                  <td>{product.category}</td>
                  <td>${product.price.toFixed(2)}</td>
                  <td>{product.stock}</td>
                  <td>
                    <div className="visibility-cell">
                      <label className="visibility-toggle">
                        <input
                          type="checkbox"
                          checked={Boolean(product.isVisible)}
                          onChange={() => handleToggleVisibility(product)}
                        />
                        <span className="visibility-slider" />
                      </label>
                      <span className={`visibility-badge ${product.isVisible ? 'visible' : 'hidden'}`}>
                        {product.isVisible ? 'Visible' : 'Hidden'}
                      </span>
                    </div>
                  </td>
                  <td>
                    <div className="table-actions">
                      <button
                        className="btn-edit"
                        onClick={() => handleEdit(product)}
                      >
                        Edit
                      </button>
                      <button
                        className="btn-delete"
                        onClick={() => setDeleteTarget(product)}
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>

        {totalPages > 1 && (
          <div className="pagination-controls">
            <button
              className="pagination-btn"
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
            >
              Previous
            </button>
            
            <div className="pagination-pages">
              {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => {
                // Show first page, last page, current page, and pages around current
                if (
                  page === 1 ||
                  page === totalPages ||
                  (page >= currentPage - 1 && page <= currentPage + 1)
                ) {
                  return (
                    <button
                      key={page}
                      className={`pagination-page ${currentPage === page ? 'active' : ''}`}
                      onClick={() => handlePageChange(page)}
                    >
                      {page}
                    </button>
                  );
                } else if (page === currentPage - 2 || page === currentPage + 2) {
                  return <span key={page} className="pagination-ellipsis">...</span>;
                }
                return null;
              })}
            </div>

            <button
              className="pagination-btn"
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
            >
              Next
            </button>
          </div>
        )}
      </div>

      <details className="product-form-card bulk-upload-card">
        <summary className="bulk-upload-summary">
          <span>Bulk Upload (Shopify CSV)</span>
          <span className="bulk-upload-hint">Optional</span>
        </summary>
        <div className="bulk-upload-body">
          <p>Upload a Shopify product CSV file. Images will be uploaded to Cloudinary. The upload runs in the background—you can keep working and we&apos;ll notify you when it finishes.</p>
          <input
            type="file"
            accept=".csv"
            onChange={(e) => setBulkFile(e.target.files?.[0] || null)}
          />
          <div style={{ marginTop: 12, display: 'flex', gap: 12, alignItems: 'center' }}>
            <button
              type="button"
              className="btn btn-secondary"
              onClick={uploadBulkProducts}
              disabled={bulkUploading || !bulkFile}
            >
              {bulkUploading ? 'Uploading...' : 'Upload CSV'}
            </button>
            {bulkResult && <span>{bulkResult}</span>}
          </div>
        </div>
      </details>

      {deleteTarget && (
        <div className="modal-overlay" onClick={() => setDeleteTarget(null)}>
          <div className="modal-card" onClick={(e) => e.stopPropagation()}>
            <h3>Delete product?</h3>
            <p>
              This will permanently remove <strong>{deleteTarget.name}</strong> from your catalog.
            </p>
            <div className="modal-actions">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => setDeleteTarget(null)}
                disabled={deleting}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-danger"
                onClick={confirmDelete}
                disabled={deleting}
              >
                {deleting ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminProducts;
